import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Grid, Typography, FormControl, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { cloneDeep } from 'lodash';
import MODULE_SUB_MODULE_OPERATIONS_TREE from './Common/MODULE_SUB_MODULE_OPERATIONS_TREE';
import VAULT_MODULE_OPERATIONS_TREE from './Common/VAULT_MODULE_OPERATION_TREE';
import { DropDown, DropdownItem } from './Common/DropDown';

const modulesSubModuleListNimbus = {};
Object.values(MODULE_SUB_MODULE_OPERATIONS_TREE).forEach((item) => {
  const access = {};
  Object.values(item?.subModules)?.forEach((subModule) => {
    access[item?.key] = 'None';
    access[`${item?.key}_${subModule?.key}`] = 'None';
  });
  modulesSubModuleListNimbus[item?.objKey] = {
    ...item,
    permissions: cloneDeep(access),
  };
});

const modulesSubModuleListVault = {};
Object.values(VAULT_MODULE_OPERATIONS_TREE).forEach((item) => {
  const access = {};
  Object.values(item?.subModules).forEach((subModule) => {
    access[item?.key] = 'None';
    access[`${item?.key}_${subModule?.key}`] = 'None';
  });
  modulesSubModuleListVault[item.objKey] = {
    ...item,
    permissions: cloneDeep(access),
  };
});

/**
 * @component GroupPermission
 * @description GroupPermission - React component for managing group permissions.
 * @param {Object} props - The props object containing the component's data and functions.
 * @param {Object} props.selectedItem - The selected item.
 * @param {Function} props.updateGroupPermission - Function to update group permissions.
 * @param {string} props.orgName - The organization ID.
 * @param {Object} props.userData - User data.
 * @param {Function} props.getDefaultPermission - Function to get default permissions.
 * @returns {JSX.Element} React component.
 */

const GroupPermission = ({
  selectedItem,
  updateGroupPermission,
  orgName,
  userData,
  getDefaultPermission,
  client,
  getMemberList,
  setUserData,
}) => {
  const theme = useTheme();
  const [updatedPermissions, setUpdatedPermissions] = useState([]);
  const [initialPermissions, setInitialPermissions] = useState([]);
  const [permissionId, setPermissionId] = useState(null);

  useEffect(() => {
    if (
      userData &&
      Array.isArray(userData?.data?.permissions) &&
      userData?.data?.permissions?.length > 0
    ) {
      const sortedPermissions = [...userData.data.permissions].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setUpdatedPermissions(cloneDeep(sortedPermissions));
      setInitialPermissions(cloneDeep(sortedPermissions));
    } else {
      getDefaultPermission(client?.id, orgName).then((res) => {
        const permissions = res?.data || [];
        // sort the permission according to names
        const sortedPermissions = [...permissions].sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setUpdatedPermissions(cloneDeep(sortedPermissions));
        setInitialPermissions(cloneDeep(sortedPermissions));
        setPermissionId(client?.id);
      });
    }
  }, [userData, client, getDefaultPermission, setPermissionId, orgName]);

  const resetPermissionHandler = useCallback(() => {
    setUpdatedPermissions(cloneDeep(initialPermissions));
  }, [initialPermissions]);

  /**
   * @function handlePermissionChange
   * @description handlePermissionChange - Handles permission change for a specific permission.
   * @param {number} index - The index of the permission in the array.
   * @param {string} value - The new permission value.
   */
  const handlePermissionChange = useCallback((index, value) => {
    setUpdatedPermissions((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], value };
      return updated;
    });
  }, []);

  const handlePermissionSubmit = useCallback(() => {
    const body = {
      name: selectedItem?.name,
      permissions: updatedPermissions,
      users: selectedItem?.users?.map((user) => user?.id),
    };
    updateGroupPermission(body, selectedItem?.id, orgName).then(() => {
      getMemberList(selectedItem?.id, orgName)
        .then((res) => {
          setUserData(res?.data);
        })
        .catch((error) => {
          return error;
        });
    });
  }, [
    updateGroupPermission,
    selectedItem,
    orgName,
    updatedPermissions,
    permissionId,
    getMemberList,
    setUserData,
  ]);

  const renderDropdown = useCallback(
    (index, value) => {
      let options = [value];
      if (value === 'Yes' || value === 'No') {
        options = ['Yes', 'No'];
      } else if (value === 'Manage' || value === 'View' || value === 'None') {
        options = ['Manage', 'View', 'None'];
      }

      return (
        <FormControl fullWidth>
          <DropDown
            sx={{ width: '200px', margin: '0 auto' }}
            value={updatedPermissions[index]?.value}
            onChange={(e) => handlePermissionChange(index, e.target.value)}
          >
            {options.map((option) => (
              <DropdownItem key={option} value={option}>
                {option}
              </DropdownItem>
            ))}
          </DropDown>
        </FormControl>
      );
    },
    [updatedPermissions, handlePermissionChange]
  );

  return (
    <Grid container display="flex" item xs={12}>
      <Grid container>
        <Grid
          container
          sx={{
            py: 2,
            borderRadius: '4px',
            marginTop: 1,
            marginBottom: 1,
          }}
          px={1}
          backgroundColor={theme.palette.other.sidebarMenuSelected}
          paddingBottom={0}
          alignItems="center"
          justifyContent="center"
          xs={12}
          textAlign="center"
        >
          <Grid item xs={8} textAlign="left">
            <Typography>Permission Names</Typography>
          </Grid>

          <Grid item xs={4} textAlign="center">
            <Typography>Permission Values</Typography>
          </Grid>
        </Grid>
        <Grid
          container
          item
          px={1}
          minHeight="250px"
          height="calc(100vh - 480px)"
          overflow="auto"
        >
          {updatedPermissions.map((perm, index) => (
            <Grid
              container
              // eslint-disable-next-line
              key={perm.name + index}
              alignItems="center"
              py={1}
            >
              <Grid item xs={8} textAlign="left">
                <Typography
                  toolTip
                  title={perm.description}
                  sx={{
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: '90%',
                    display: 'block',
                  }}
                >
                  {perm.name}
                </Typography>
              </Grid>
              <Grid
                item
                xs={4}
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                {renderDropdown(index, perm.value)}
              </Grid>
            </Grid>
          ))}
        </Grid>
        <Grid
          container
          justifyContent="space-between"
          py="12px"
          mx="-24px"
          width="calc(100% + 48px)"
          borderTop={`1px solid ${theme.palette.other.sidebarMenuSelected}`}
        >
          {!(userData?.data?.permissions || []).length && (
            <Grid item xs={8} pl={4} display="flex" alignItems="center">
              <Typography
                variant="subtitle2"
                color={theme.palette.primary.main}
                sx={{ fontStyle: 'italic' }}
              >
                Note: Please save the permissions for the newly created group.
              </Typography>
            </Grid>
          )}
          <Grid
            item
            xs={!(userData?.data?.permissions || []).length ? 4 : 12}
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
          >
            <Box mr="12px">
              <Button
                variant="outlined"
                onClick={resetPermissionHandler}
                title="Reset"
              >
                Reset
              </Button>
            </Box>
            <Box mr="24px">
              <Button variant="contained" onClick={handlePermissionSubmit}>
                Submit
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

GroupPermission.propTypes = {
  selectedItem: PropTypes.oneOfType([PropTypes.object]).isRequired,
  client: PropTypes.oneOfType([PropTypes.object]).isRequired,
  updateGroupPermission: PropTypes.func.isRequired,
  orgName: PropTypes.string.isRequired,
  userData: PropTypes.oneOfType([PropTypes.object]).isRequired,
  getDefaultPermission: PropTypes.func.isRequired,
  getMemberList: PropTypes.func.isRequired,
  setUserData: PropTypes.func.isRequired,
};

export default GroupPermission;
